import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Share2, Copy, Check, Trash2, Loader2, Shield, Linkedin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * ShareAssessmentModal - opens from the completed Live Interview ReportPanel.
 * Lets the owner mint a public read-only URL for the 7-part Hiring Assessment
 * with an optional PII-redaction toggle, and revoke at any time.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - sessionId: string (video_session_id)
 */
export default function ShareAssessmentModal({ open, onClose, sessionId }) {
  const [redact, setRedact] = useState(true);
  const [shareId, setShareId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) {
      setShareId(null);
      setBusy(false);
      setCopied(false);
      setCaptionCopied(false);
      setErr("");
    }
  }, [open]);

  if (!open) return null;

  const publicUrl = shareId
    ? `${process.env.REACT_APP_BACKEND_URL}/api/video-interview/share-preview/${shareId}`
    : "";

  const generate = async (redactValue) => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/video-interview/${sessionId}/share`, {
        redact_pii: redactValue,
      });
      setShareId(data.share_id);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Could not create share link.");
    } finally {
      setBusy(false);
    }
  };

  const toggleRedact = async (v) => {
    setRedact(v);
    if (shareId) {
      // Update existing share
      await generate(v);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  // Pre-written LinkedIn caption. We copy this to the clipboard *before*
  // opening LinkedIn's composer so the user can just paste (LinkedIn's
  // official share intent no longer honours a `text` param reliably).
  const linkedInCaption = [
    "Just completed an AI-powered interview assessment on Intuiterview.",
    "",
    "Got scored on communication, structure, depth, and role fit, with actionable coaching for the next round.",
    "",
    "See the full read-only assessment here:",
    publicUrl,
    "",
    "Preparing like it's real.",
    "#InterviewPrep #CareerGrowth #Intuiterview",
  ].filter((l) => l !== undefined).join("\n");

  const shareToLinkedIn = async () => {
    if (!publicUrl) return;
    // Best-effort: pre-copy caption so the user can paste into LinkedIn's
    // composer. Then open LinkedIn's official share-offsite intent - it will
    // preview the shared URL's OG tags and let the user add commentary.
    try {
      await navigator.clipboard.writeText(linkedInCaption);
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 4000);
    } catch { /* clipboard blocked without user gesture, non-fatal */ }
    const intent =
      "https://www.linkedin.com/sharing/share-offsite/?url=" +
      encodeURIComponent(publicUrl);
    window.open(intent, "_blank", "noopener,noreferrer");
  };

  const revoke = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.delete(`/video-interview/${sessionId}/share`);
      setShareId(null);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Could not revoke.");
    } finally {
      setBusy(false);
    }
  };

  // Portal to <body>: guarantees the fixed overlay always covers the viewport
  // even when an ancestor has transform/filter/backdrop-filter (which would
  // otherwise clip the modal into invisibility - notably on Safari).
  return createPortal(
    <div
      data-testid="share-assessment-modal"
      className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-md flex items-center justify-center p-4 animate-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-zinc-800/70 bg-zinc-950 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="share-modal-close"
          className="absolute top-3.5 right-3.5 text-zinc-500 hover:text-zinc-100 transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400">
          <Share2 className="h-3 w-3" /> Share Assessment
        </div>
        <h2 className="mt-2 text-xl font-bold text-zinc-50 leading-tight">
          Send a read-only link to your Assessment.
        </h2>
        <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
          Anyone with the link can view scores, strengths, and coaching. No login required.
          You can revoke it anytime.
        </p>

        {/* Redact toggle */}
        <div className="mt-5 rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-4 flex items-start gap-3">
          <div className="h-8 w-8 rounded-full border border-amber-500/30 bg-amber-500/[0.08] flex items-center justify-center flex-shrink-0">
            <Shield className="h-3.5 w-3.5 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-zinc-100">Hide company name</div>
              <button
                onClick={() => toggleRedact(!redact)}
                disabled={busy}
                data-testid="share-redact-toggle"
                role="switch"
                aria-checked={redact}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-60 ${
                  redact ? "bg-amber-500" : "bg-zinc-700"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    redact ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
            <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
              Recommended when sharing with recruiters or on social media.
            </div>
          </div>
        </div>

        {err && (
          <div
            data-testid="share-error"
            className="mt-4 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-lg px-3 py-2"
          >
            {err}
          </div>
        )}

        {/* Generate / URL display */}
        {!shareId ? (
          <button
            onClick={() => generate(redact)}
            disabled={busy}
            data-testid="share-generate-btn"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2.5 shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)] disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
            Generate share link
          </button>
        ) : (
          <div className="mt-5 space-y-3">
            <div
              data-testid="share-url-display"
              className="flex items-stretch gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.04] p-2"
            >
              <input
                readOnly
                value={publicUrl}
                data-testid="share-url-input"
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-transparent text-xs text-zinc-100 px-2 py-1 outline-none min-w-0 truncate"
              />
              <button
                onClick={copy}
                data-testid="share-copy-btn"
                className="inline-flex items-center gap-1.5 text-xs bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-md px-3 py-1.5 flex-shrink-0"
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="flex items-center justify-between gap-3">
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="share-preview-link"
                className="text-xs text-amber-300 hover:text-amber-200 underline underline-offset-4 decoration-amber-500/40"
              >
                Preview public view →
              </a>
              <button
                onClick={revoke}
                disabled={busy}
                data-testid="share-revoke-btn"
                className="inline-flex items-center gap-1.5 text-xs text-rose-300 hover:text-rose-200 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                Revoke link
              </button>
            </div>

            {/* LinkedIn share, copies a pre-written caption and opens the
                LinkedIn share composer. LinkedIn's official intent only
                accepts the URL, so we pre-copy the caption for the user
                to paste in one gesture. */}
            <button
              onClick={shareToLinkedIn}
              data-testid="share-linkedin-btn"
              className="w-full inline-flex items-center justify-center gap-2 text-sm bg-[#0A66C2] hover:bg-[#004182] text-white font-semibold rounded-lg px-4 py-2.5 shadow-[0_0_24px_-6px_rgba(10,102,194,0.55)] transition-colors"
            >
              <Linkedin className="h-4 w-4" />
              Share on LinkedIn
            </button>
            {captionCopied && (
              <div
                data-testid="share-linkedin-hint"
                className="text-[11px] text-emerald-300 bg-emerald-500/[0.08] border border-emerald-500/25 rounded-lg px-3 py-2 leading-relaxed"
              >
                <Check className="h-3 w-3 inline mr-1 -mt-0.5" />
                Caption copied. Paste it into the LinkedIn composer that just opened.
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
